import { SphereIconButton } from "@components/common/sphere-icon-button";
import { PALETTE_VARS } from "@styles/palette-vars";
import { BaseDialog, CircularProgress, CloseIcon, FontWeights, HEX_ALPHA, Text } from "@flat-ui";
import { Box } from "@mui-wrapper";
import { AttachmentItemActions } from "@pages/project-details/project-markups/sidepanel/attachment/attachment-item-actions";
import { useRefreshAttachment } from "@pages/project-details/project-markups/sidepanel/use-refresh-attachment";
import { CommonStyles, withEllipsis } from "@styles/common-styles";
import { useEffect, useState } from "react";
/** Size in pixels for each attachment action */
const ACTION_BTN_SIZE = "1.5rem";

/** Size in pixels for each attachment action icon */
const ACTION_ICON_SIZE = "1.5rem";

/**
 * This component renders a full-screen dialog to preview an attachment. It displays the attachment's image,
 * name, and provides actions for downloading or deleting the attachment.
 */
export function FaroImagePreview({
  attachment,
  isDownloading,
  setIsPreviewDialogOpen,
  setIsDownloading,
  setSelectedAttachments
}) {
  const {
    refreshAttachmentIfExpired,
    isAttachmentExpired
  } = useRefreshAttachment();
  const [currentAttachment, setCurrentAttachment] = useState(attachment);
  const [isImageLoading, setIsImageLoading] = useState(false);
  useEffect(() => {
    async function checkAndUpdateAttachment() {
      try {
        if (isAttachmentExpired(attachment)) {
          setIsImageLoading(true);
          const updatedAttachment = await refreshAttachmentIfExpired([attachment.id]);
          setCurrentAttachment(updatedAttachment[0]);
          setIsImageLoading(false);
        }
      } catch {
        setIsImageLoading(false);
      }
    }
    void checkAndUpdateAttachment();
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps -- The empty array ensures it runs only once, on mount
  }, []);
  return (
    // `BaseDialog` rather than `Dialog`: this is a full-screen surface with a layout and actions
    // of its own, which the structured dialog's title-plus-button-row API cannot express.
    <BaseDialog open fullScreen slotProps={{
      paper: {
        sx: {
          backgroundColor: "transparent"
        }
      }
    }}>
      <Box data-testid="preview-attachment-dialog-content" sx={{
        height: "calc(100vh - 2rem)",
        display: "flex",
        flexDirection: "column"
      }}>
        <Box sx={{
          display: "flex",
          height: "70px",
          alignItems: "center",
          boxSizing: "border-box",
          borderBottom: CommonStyles.Borders.divider,
          backgroundColor: "background.paper",
          zIndex: 1,
          position: "relative",
          justifyContent: "space-between",
          width: "100%",
          px: "1rem"
        }}>
          <Box sx={{
            display: "flex",
            alignItems: "center",
            overflow: "hidden"
          }}>
            <SphereIconButton dataTestId="preview-dialog-close-icon" component={CloseIcon} buttonSize={ACTION_BTN_SIZE} iconSize={ACTION_ICON_SIZE} iconButtonProps={{
              sx: {
                padding: "20px"
              }
            }} onClick={() => setIsPreviewDialogOpen(false)} />
            <Text variant="heading16" sx={{
              marginLeft: "10px",
              fontWeight: FontWeights.Bold,
              ...withEllipsis
            }} title={currentAttachment.name}>
              {currentAttachment.name}
            </Text>
          </Box>
          <Box sx={{
            display: "flex",
            alignItems: "center",
            flexShrink: 0
          }}>
            {isDownloading ? <CircularProgress size={"1rem"} /> : <AttachmentItemActions attachment={currentAttachment} setIsDownloading={setIsDownloading} eventSource="preview attachment" buttonSize={ACTION_BTN_SIZE} iconSize={ACTION_ICON_SIZE} iconBtnProps={{
              sx: {
                padding: "20px"
              }
            }} setSelectedAttachments={setSelectedAttachments} />}
          </Box>
        </Box>

        <Box sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flex: 1,
          padding: {
            xs: "16px",
            md: "24px"
          },
          // Dark in both colour schemes: the preview is a lightbox, not a themed surface
          backgroundColor: theme => theme.alpha(theme.vars.palette.common.black, HEX_ALPHA.alpha90),
          backdropFilter: "blur(15px)",
          overflow: "auto"
        }}>
          {isImageLoading ? <CircularProgress size={"2rem"} sx={{
            color: PALETTE_VARS.common.white
          }} /> : <Box component="img" src={currentAttachment.signedUri} alt="attachment annotation" sx={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            width: "auto",
            height: "auto"
          }} />}
        </Box>
      </Box>
    </BaseDialog>
  );
}